import {
  Callout,
  DataTable,
  Heading,
  Lettered,
  Ph,
  Plain,
  Term,
} from "./legalBlocks";

export const TERMS_GLANCE = [
  [
    "Sayagie is a marketplace",
    <>
      Your contract for the goods is with the supplier. We provide the platform, take the
      payment and support the order.
    </>,
  ],
  ["You must be 18", <>LPG is a hazardous product. Accounts are for adults only.</>],
  [
    "Three ways to pay",
    <>Cash on delivery, Sayagie wallet, or card and mobile money via Paystack.</>,
  ],
  [
    "Cancel before dispatch",
    <>Free until the supplier dispatches. After that a fee may apply.</>,
  ],
  [
    "Reviews need a completed order",
    <>Only a customer whose order was completed can review that supplier.</>,
  ],
  [
    "Ghanaian law applies",
    <>Disputes go to the courts of Ghana, after a good-faith attempt to settle.</>,
  ],
];

const TERMS_SECTIONS = [
  {
    id: "t1",
    n: "1",
    title: "This agreement",
    body: (
      <>
        <p>
          These Terms are a binding agreement between you and Sayaty Enterprise (
          <Term>Sayagie</Term>, <Term>we</Term>, <Term>us</Term>). They apply every time
          you use the Sayagie app or website.
        </p>
        <p>
          By creating an account, placing an order, or continuing to use the Services, you
          accept these Terms and the Privacy Policy, which forms part of them. If you do
          not accept them, do not use the Services.
        </p>
      </>
    ),
  },
  {
    id: "t2",
    n: "2",
    title: "Definitions",
    body: (
      <Plain
        items={[
          <>
            <Term>Services</Term> — the Sayagie apps, website, accounts, wallet and
            support.
          </>,
          <>
            <Term>Supplier</Term> — an independent business listed on Sayagie that sells
            and delivers the goods.
          </>,
          <>
            <Term>Goods</Term> — LPG in cylinders, cylinder exchanges, accessories and any
            other item a Supplier lists.
          </>,
          <>
            <Term>Order</Term> — your request to buy Goods from a Supplier through the
            Services.
          </>,
          <>
            <Term>Wallet</Term> — the prepaid balance held in your Sayagie account.
          </>,
          <>
            <Term>Order Total</Term> — the price of the Goods plus delivery, service fees
            and any taxes or levies shown at checkout.
          </>,
        ]}
      />
    ),
  },
  {
    id: "t3",
    n: "3",
    title: "Eligibility and your account",
    body: (
      <>
        <p>
          You must be at least 18 years old, legally capable of entering a contract, and
          located in an area Sayagie serves. Business accounts must be opened by someone
          authorised to bind that business.
        </p>
        <p>
          You agree to give accurate information, keep it current, and keep your password
          and device secure. You are responsible for everything done through your account,
          unless you have told us it was compromised and we had a reasonable opportunity
          to act. Tell us immediately at <Ph>[support email]</Ph> if you suspect
          unauthorised use.
        </p>
        <p>
          One person, one account. We may refuse, suspend or close duplicate accounts, and
          accounts opened with false details.
        </p>
      </>
    ),
  },
  {
    id: "t4",
    n: "4",
    title: "What Sayagie is — and is not",
    body: (
      <>
        <Callout title="The most important clause in this document">
          Sayagie is a marketplace and an agent for collecting payment. When you place an
          Order, the contract to sell and deliver the Goods is formed{" "}
          <strong>between you and the Supplier</strong>, not with Sayagie. We are not the
          seller, the manufacturer, the filler or the importer of the Goods.
        </Callout>
        <p>
          What Sayagie is responsible for: operating the platform, displaying Supplier
          listings accurately as supplied to us, taking your payment securely, passing
          your Order to the Supplier, giving you order tracking and records, and running
          the support and dispute process described in section 11.
        </p>
        <p>
          What the Supplier is responsible for: the quality, safety, weight, fill level
          and legal compliance of the Goods; holding the licences the National Petroleum
          Authority and any other regulator require; the condition and certification of
          cylinders; and delivering the Order as accepted.
        </p>
        <p>
          Nothing in this section removes any right you have against us or against a
          Supplier under Ghana’s Sale of Goods Act, 1962 (Act 137) or other mandatory
          consumer protection law.
        </p>
      </>
    ),
  },
  {
    id: "t5",
    n: "5",
    title: "Placing an Order",
    body: (
      <>
        <p>
          Listings are an invitation to treat, not an offer. Your Order is an offer to
          buy. A contract forms only when the Supplier accepts the Order, which the app
          shows as the order moving from <Term>PENDING</Term> to <Term>ACCEPTED</Term>.
        </p>
        <p>
          A Supplier may decline an Order — because stock ran out, the address is outside
          its delivery zone, or it cannot meet the time window. If an Order is declined
          after you have paid, you get a full refund to the original payment method or
          your Wallet, as set out in section 11.
        </p>
        <p>
          You must give an accurate delivery address and a phone number that will be
          answered. Repeated failed deliveries caused by a wrong address or an unreachable
          phone may be charged for.
        </p>
      </>
    ),
  },
  {
    id: "t6",
    n: "6",
    title: "Prices, fees and taxes",
    body: (
      <>
        <p>
          Prices are set by Suppliers and shown in Ghana Cedis (GHS) unless another
          currency is displayed. The price you pay is the Order Total shown on the Order
          Review screen immediately before you confirm — that figure is itemised, and it
          includes the Goods, delivery, any Sayagie service fee and all applicable taxes
          and levies.
        </p>
        <p>
          LPG prices move with regulated and market rates. A price shown earlier in a
          session may change before you confirm; only the figure on the confirmation
          screen binds. Once an Order is accepted, the price is fixed.
        </p>
        <p>
          We may introduce or change service fees. Changes apply to Orders placed after
          the new fee is displayed, never retrospectively.
        </p>
      </>
    ),
  },
  {
    id: "t7",
    n: "7",
    title: "Paying",
    body: (
      <>
        <DataTable
          head={["Method", "How it works", "When you are charged"]}
          rows={[
            [
              "Cash on delivery",
              "You pay the rider in cash on handover. Please have the exact amount; riders may not carry change.",
              "At delivery",
            ],
            [
              "Wallet",
              "The Order Total is deducted from your Wallet balance in the matching currency. If the balance is short, the app offers a top-up for the difference before the Order is placed.",
              "On placing the Order",
            ],
            [
              "Card or mobile money",
              "Paystack’s secure checkout opens. You authorise the payment there, and the app confirms the Order once Paystack reports success.",
              "On placing the Order",
            ],
          ]}
        />
        <p>
          If a payment is authorised but the Order cannot be created, we refund it
          automatically. If a payment’s status is unclear, we hold the Order until
          Paystack confirms the outcome, and we tell you which way it resolved.
        </p>
        <p>
          Refusing to pay on delivery under a cash Order, or repeatedly failing to accept
          cash Orders, may lead to cash on delivery being withdrawn from your account.
        </p>
      </>
    ),
  },
  {
    id: "t8",
    n: "8",
    title: "Your Wallet",
    body: (
      <>
        <p>
          The Wallet is a prepaid balance for buying on Sayagie. It is{" "}
          <strong>not a bank account or a deposit</strong>, it is not covered by any
          deposit protection scheme, and it earns no interest.
        </p>
        <Plain
          items={[
            "Top up by card or mobile money through Paystack. Funds appear once Paystack confirms the payment.",
            "Balances are held per currency and can only be spent in that currency.",
            "The Wallet cannot go negative and cannot be transferred to another user.",
            "Refunds credited to the Wallet are available immediately for new Orders.",
            "You can withdraw an eligible balance under section 9.",
          ]}
        />
        <p>
          We may freeze a Wallet where we reasonably suspect fraud, money laundering or
          unauthorised access. We will tell you, unless the law prevents us, and we will
          release it as soon as the concern is resolved.
        </p>
      </>
    ),
  },
  {
    id: "t9",
    n: "9",
    title: "Withdrawals and payouts",
    body: (
      <>
        <p>
          To withdraw, add a withdrawal account — a bank account or mobile money wallet in
          your own name — and submit a request. The account holder name must match your
          Sayagie account; we reject third-party payouts.
        </p>
        <p>
          Requests are reviewed before release. We normally process within{" "}
          <Ph>[processing window]</Ph> business days, after which the receiving bank or
          mobile money operator sets its own timing. A minimum withdrawal of{" "}
          <Ph>[minimum amount]</Ph> applies, and the provider’s transfer fee may be
          deducted.
        </p>
        <p>
          We may decline or delay a withdrawal where the account details are unverifiable,
          an Order dispute is open, or we are required to carry out identity or
          source-of-funds checks. If we ask you for verification documents, we handle them
          under the Privacy Policy.
        </p>
      </>
    ),
  },
  {
    id: "t10",
    n: "10",
    title: "Delivery, cylinders and safety",
    body: (
      <>
        <p>
          Delivery windows are estimates. Traffic, weather, depot queues and refill
          availability affect them, and a missed estimate is not by itself a breach.
        </p>
        <p>
          Someone aged 18 or over must be present to receive an LPG delivery and to sign
          or confirm handover. If nobody is available, the rider will attempt contact and
          may return the Goods; a redelivery fee may apply.
        </p>
        <p>
          On a cylinder exchange, you must hand over a cylinder of the same type and in a
          condition the Supplier accepts. A cylinder that is damaged, out of
          certification, or not the Supplier’s to accept may be refused, and the exchange
          will be re-quoted as an outright purchase.
        </p>
        <Callout title="Safety — your obligations">
          Check for leaks before use, and never test with a flame. Keep cylinders upright,
          outdoors or in a ventilated space, away from heat, drains and basements. Use
          only a regulator and hose rated for the cylinder. Do not attempt to refill,
          decant or modify a cylinder. If you smell gas, close the valve, do not operate
          electrical switches, ventilate the area and leave, then call your Supplier or
          the emergency services.
        </Callout>
        <p>
          Once delivery is complete, the Goods are at your risk and you are responsible
          for storing and using them safely. We are not liable for loss or injury caused
          by unsafe storage, unsafe use, or equipment not supplied through the Services.
        </p>
      </>
    ),
  },
  {
    id: "t11",
    n: "11",
    title: "Cancellations, returns and refunds",
    body: (
      <>
        <Heading>Cancelling</Heading>
        <p>
          You can cancel free of charge at any time before the Supplier dispatches the
          Order. After dispatch, a cancellation fee of up to <Ph>[cancellation fee]</Ph>{" "}
          may be charged to cover the Supplier’s costs. Once delivery is complete, an
          Order cannot be cancelled — raise a return or a complaint instead.
        </p>
        <Heading>When we refund in full</Heading>
        <Plain
          items={[
            "The Supplier declines or cancels the Order.",
            "The Order is not delivered within the agreed window and you no longer want it.",
            "The Goods are the wrong item, short-filled, or arrive damaged.",
            "You were charged twice, or charged for an Order that was never created.",
          ]}
        />
        <Heading>Raising a problem</Heading>
        <p>
          Report a problem with an Order within <Ph>[complaint window, e.g. 48 hours]</Ph>{" "}
          of delivery, through the Order Details screen or by contacting support. Photos
          help and we may ask for them. We will investigate with the Supplier and give you
          a decision within <Ph>[decision window]</Ph> business days.
        </p>
        <Heading>How refunds arrive</Heading>
        <p>
          Approved refunds go to your Wallet immediately, or back to the original payment
          method on request. A card or mobile money reversal is subject to your provider’s
          timing, typically <Ph>[refund timing]</Ph> business days after we release it.
          Cash on delivery refunds are paid to your Wallet or to a withdrawal account.
        </p>
        <p>
          For safety reasons, LPG that has been delivered, connected or partially used
          cannot be returned for a change of mind.
        </p>
      </>
    ),
  },
  {
    id: "t12",
    n: "12",
    title: "Ratings and reviews",
    body: (
      <>
        <p>
          You may rate and review a Supplier only for an Order that was completed on your
          own account. This keeps reviews tied to real transactions, and it is why the
          option appears on Order Details rather than on a Supplier’s profile.
        </p>
        <p>
          Reviews are published with your display name and profile photo. Keep them honest
          and about your own experience. We may remove or refuse a review that is abusive,
          discriminatory, defamatory, contains personal data about someone else, promotes
          another business, or was written in exchange for payment — and we may suspend
          accounts that post them.
        </p>
        <p>
          We do not edit reviews to make a Supplier look better. Suppliers may respond
          publicly.
        </p>
        <p>
          By posting, you grant Sayagie a non-exclusive, royalty-free, worldwide licence
          to display, reproduce and distribute your review in connection with the
          Services. You keep ownership of what you wrote.
        </p>
      </>
    ),
  },
  {
    id: "t13",
    n: "13",
    title: "Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <Lettered
          items={[
            "use the Services for anything unlawful, or to resell LPG without the licences the law requires;",
            "order on behalf of someone under 18, or for an address you know to be unsafe for LPG;",
            "submit false, fraudulent or duplicate Orders, chargebacks or refund claims;",
            "impersonate anyone, or use another person’s payment method without authority;",
            "scrape, crawl, reverse-engineer, decompile or interfere with the Services, or attempt to access another user’s account;",
            "introduce malware, or place unreasonable load on our systems; or",
            "harass, threaten or abuse a rider, a Supplier’s staff, or our team.",
          ]}
        />
        <p>
          Abuse directed at a rider or support agent will end the account, immediately and
          without refund of any fee paid for a service not yet delivered.
        </p>
      </>
    ),
  },
  {
    id: "t14",
    n: "14",
    title: "Intellectual property",
    body: (
      <>
        <p>
          The Sayagie name, logo, app, website, designs, text, graphics and software are
          owned by us or our licensors and are protected by copyright and trade mark law.
          We grant you a personal, limited, non-transferable, revocable licence to use the
          app for its intended purpose. You may not copy, modify, distribute, sell or
          create derivative works from any part of the Services.
        </p>
        <p>
          Supplier names, logos and product images belong to their owners and appear under
          licence to us.
        </p>
      </>
    ),
  },
  {
    id: "t15",
    n: "15",
    title: "Suspension, deactivation and closure",
    body: (
      <>
        <p>
          You can deactivate your account at any time in Account Settings. Deactivation
          hides your profile and signs you out; signing in again reactivates it. Ask us at{" "}
          <Ph>[privacy email]</Ph> if you want your data erased rather than retained — see
          section 13 of the Privacy Policy.
        </p>
        <p>
          We may suspend or close an account, with notice where practicable, if you
          materially breach these Terms, if we reasonably suspect fraud or illegality, if
          you abuse our people, or if we are required to by law or a regulator.
        </p>
        <p>
          On closure: outstanding Orders are completed or refunded, and any remaining
          Wallet balance you are entitled to is returned to you, less amounts you owe.
          Sections 12, 14, 16, 17, 18 and 19 survive closure.
        </p>
      </>
    ),
  },
  {
    id: "t16",
    n: "16",
    title: "Availability and disclaimers",
    body: (
      <>
        <p>
          We work to keep Sayagie available, but we do not promise uninterrupted or
          error-free service. Maintenance, network failures, payment provider outages and
          events outside our reasonable control can interrupt it.
        </p>
        <p>
          Except as this agreement expressly states, and to the extent the law allows, the
          Services are provided “as is” and we exclude all implied warranties. We do not
          warrant that a Supplier will be available in your area, that a price will hold,
          or that a delivery estimate will be met.
        </p>
        <p>
          Nothing here excludes liability for death or personal injury caused by our
          negligence, for fraud, or for anything else that cannot lawfully be excluded.
        </p>
      </>
    ),
  },
  {
    id: "t17",
    n: "17",
    title: "Our liability",
    body: (
      <>
        <p>
          Subject to section 16, our total liability to you for all claims connected to an
          Order is limited to the greater of the Order Total for that Order and{" "}
          <Ph>[liability cap]</Ph>. For claims not connected to a specific Order, our
          total liability in any twelve-month period is limited to the total amount of
          Sayagie service fees you paid us in that period.
        </p>
        <p>
          We are not liable for indirect or consequential loss, loss of profit, loss of
          business, or loss arising from a Supplier’s acts or omissions — including the
          quality or safety of the Goods, for which the Supplier is responsible under
          section 4.
        </p>
      </>
    ),
  },
  {
    id: "t18",
    n: "18",
    title: "Indemnity",
    body: (
      <p>
        You agree to indemnify Sayagie against claims, losses and reasonable legal costs
        arising from your breach of these Terms, your misuse of the Services, your unsafe
        storage or use of LPG, or content you post that infringes someone else’s rights.
      </p>
    ),
  },
  {
    id: "t19",
    n: "19",
    title: "Disputes and governing law",
    body: (
      <>
        <p>
          Come to us first. Contact <Ph>[support email]</Ph> with the Order number and
          what went wrong; most disputes are resolved there within days.
        </p>
        <p>
          If that does not settle it, both parties agree to attempt resolution in good
          faith for 30 days before starting proceedings, including by mediation if either
          party proposes it.
        </p>
        <p>
          These Terms are governed by the laws of the Republic of Ghana, and the courts of
          Ghana have exclusive jurisdiction. This does not deprive you of the protection
          of mandatory consumer law in your place of residence.
        </p>
      </>
    ),
  },
  {
    id: "t20",
    n: "20",
    title: "Changes, and the rest",
    body: (
      <>
        <p>
          <Term>Changes.</Term> We may update these Terms. We will post the new version
          here with a new date, and for material changes we will notify you in the app or
          by email at least 14 days beforehand. Orders placed before a change are governed
          by the Terms in force when you placed them.
        </p>
        <p>
          <Term>Assignment.</Term> You may not transfer your rights under this agreement.
          We may transfer ours to a group company or an acquirer, provided your rights are
          not reduced.
        </p>
        <p>
          <Term>Severability.</Term> If a clause is held unenforceable, the rest stays in
          force.
        </p>
        <p>
          <Term>No waiver.</Term> If we do not enforce a right immediately, we do not give
          it up.
        </p>
        <p>
          <Term>Entire agreement.</Term> These Terms and the Privacy Policy are the whole
          agreement between us about the Services.
        </p>
        <p>
          <Term>Notices.</Term> We will contact you at the email address or phone number
          on your account. Write to us at the addresses in section 21.
        </p>
      </>
    ),
  },
  {
    id: "t21",
    n: "21",
    title: "Contact",
    body: (
      <DataTable
        head={["Channel", "Where to write"]}
        rows={[
          [
            "Customer support",
            <>
              <Ph>[support email]</Ph>, <Ph>[support phone]</Ph>
            </>,
          ],
          [
            "Legal notices",
            <>
              <Ph>[legal email]</Ph>, <Ph>[registered office address]</Ph>
            </>,
          ],
          [
            "Company",
            <>
              Sayaty Enterprise, reg. no. <Ph>[company reg. no.]</Ph>
            </>,
          ],
        ]}
      />
    ),
  },
  {
    id: "t22",
    n: "22",
    title: "Before you publish",
    internal: true,
    body: (
      <>
        <p>Fill each of these, then delete this section:</p>
        <Plain
          items={[
            "Company registration number and registered office address (1, 21)",
            "Support email and phone (3, 19, 21)",
            "Legal notices email (21)",
            "Withdrawal processing window and minimum amount (9)",
            "Cancellation fee after dispatch (11)",
            "Complaint window, decision window and refund timing (11)",
            "Liability cap (17)",
            "Effective date (masthead)",
            "Settle every commercial number above with finance and legal — they are business decisions, not drafting choices",
          ]}
        />
      </>
    ),
  },
];

export default TERMS_SECTIONS;
